import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import {
  DateFormatPipe,
  DateSincePipe,
  DurationPipe,
  ECEFToWGS84Pipe,
  ItemNamePipe,
  LinkifyPipe,
  MentionifyPipe,
  SentenceCasePipe,
} from './pipes/tools.pipe';
import { ProductPermissionPipe } from './pipes/product.pipe';
import {
  UserConfigurationErrorDialog
} from './dialogs/user-configuration-error-dialog/user-configuration-error.dialog';
import { ConfirmDialog } from './dialogs/confirm-dialog/confirm.dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SearchPipe } from './pipes/search.pipe';
import { CesiumDirective } from './directives/cesium';
import { CesiumMiniMapDirective } from './directives/cesium-minimap';
import {
  Cesium2dvControlComponent
} from './components/cesium-2dv-control/cesium-2dv-control.component';
import { InspectionModulePipe } from './pipes/module.pipe';
import {
  DetectionConfidencePipe, DetectionEvolutionPipe,
  DetectionLabelPipe, DetectionStatusPipe,
  DistinctDetectionCreateModesPipe,
  DistinctDetectionLabelsPipe,
  FormatDetectionModePipe,
  FormatDetectionStatusPipe
} from './pipes/detection.pipe';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import {
  DetectionListComponent
} from './components/item-lists/detection-list/detection-list.component';
import {
  MarkerListComponent
} from './components/item-lists/marker-list/marker-list.component';
import {
  CutListComponent
} from './components/item-lists/cut-list/cut-list.component';
import {
  MeasureListComponent
} from './components/item-lists/measure-list/measure-list.component';
import {
  VolumeListComponent
} from './components/item-lists/volume-list/volume-list.component';
import { MatListModule } from '@angular/material/list';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatBadgeModule } from '@angular/material/badge';
import {
  VolumeColorRampComponent
} from './components/volume-color-ramp/volume-color-ramp.component';
import { NgChartsModule } from 'ng2-charts';
import {
  DetectionCardComponent
} from './components/item-cards/detection-card/detection-card.component';
import {
  VolumeCardComponent
} from './components/item-cards/volume-card/volume-card.component';
import {
  VolumeDisplayModeSelectorComponent
} from './components/volume-display-mode/volume-display-mode-selector.component';
import {
  VolumeThresholdFormComponent
} from './components/volume-threshold-form/volume-threshold-form.component';
import {
  CutChartComponent
} from './components/charts/cut-chart.component';
import {
  CutDetailsComponent
} from './components/item-details/cut-details/cut-details.component';
import {
  ComputeLoaderComponent
} from './components/loaders/compute-loader/compute-loader.component';
import {
  MeasureDetailsComponent
} from './components/item-details/measure-details/measure-details.component';
import {
  DetectionDetailsComponent
} from './components/item-details/detection-details/detection-details.component';
import {
  MarkerDetailsComponent
} from './components/item-details/marker-details/marker-details.component';
import {
  VolumeDetailsComponent
} from './components/item-details/volume-details/volume-details.component';
import {
  MeasureCardComponent
} from './components/item-cards/measure-card/measure-card.component';
import {
  CutCardComponent
} from './components/item-cards/cut-card/cut-card.component';
import {
  MarkerCardComponent
} from './components/item-cards/marker-card/marker-card.component';
import {
  DensityChartComponent
} from './components/charts/density-chart.component';
import { FiltersPipe } from './pipes/filters.pipe';
import { SectorPipe } from './pipes/sector.pipe';
import {
  FilterSliderComponent
} from './components/filter-slider/filter-slider.component';
import { MatSliderModule } from '@angular/material/slider';
import { MatMenuModule } from '@angular/material/menu';
import {
  HistoryChannelComponents
} from './components/history-channel/history-channel.components';
import {
  DeleteConfirmDialog
} from './dialogs/delete-confirm-dialog/delete-confirm.dialog';
import { RenameDialog } from './dialogs/rename-dialog/rename.dialog';
import { SearchComponent } from './components/search/search.component';
import {
  SiteSearchComponent
} from './components/search/site-search/site-search.component';
import {
  DeleteDetectionDialog
} from './dialogs/delete-detection-dialog/delete-detection.dialog';
import {
  CreateDetectionDialog
} from './dialogs/create-detection-dialog/create-detection.dialog';
import { ReportDialog } from './dialogs/report-dialog/report.dialog';
import {
  LeaveSiteDialog
} from './dialogs/leave-site-dialog/leave-site.dialog';
import { MarkerSortDistancePipe } from './pipes/marker.pipe';
import { MatTabsModule } from '@angular/material/tabs';
import {
  ItemSearchComponent
} from './components/search/item-search/item-search.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
  OrganizationDialog
} from './dialogs/organization-dialog/organization.dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslocoModule } from '@jsverse/transloco';
import {
  AdvandcedActionStartDialog
} from './dialogs/advandced-action-start-dialog/advandced-action-start.dialog';
import {
  AdvandcedActionStopDialog
} from './dialogs/advandced-action-stop-dialog/advandced-action-stop.dialog';
import {
  CutHistoryChannelComponents
} from './components/history-channel/cut-history-channel.component';
import {
  MeasureHistoryChannelComponents
} from './components/history-channel/measure-history-channel.component';
import {
  BatchDiffRepresentationSelectorDialog
} from './dialogs/batch-diff-representation-selector-dialog/batch-diff-representation-selector.dialog';
import {
  ElevationListComponent
} from './components/item-lists/elevation-list/elevation-list.component';
import {
  ElevationCardComponent
} from './components/item-cards/elevation-card/elevation-card.component';
import {
  ElevationDetailsComponent
} from './components/item-details/elevation-details/elevation-details.component';
import {
  ElevationChartComponent
} from './components/charts/elevation-chart.component';
import {
  TilesetDiffColorRampComponent
} from './components/tileset-diff-color-ramp/tileset-diff-color-ramp.component';
import {
  TilesetStyleFormComponent
} from './components/tileset-style-form/tileset-style-form.component';
import {
  ReferenceBatchSelectorComponent
} from './components/reference-batch-selector/reference-batch-selector.component';
import {
  ViewRepresentationSelectorComponent
} from './components/view-configuration/view-representation-selector/view-representation-selector.component';
import {
  ViewModeSelectorComponent
} from './components/view-configuration/view-mode-selector/view-mode-selector.component';
import {
  WorldActionsBarComponent
} from './components/world-actions-bar/world-actions-bar.component';
import {
  CreatePerreadPipelineDialog
} from './dialogs/create-perread-pipeline-dialog/create-perread-pipeline.dialog';
import {
  VolumeSummaryComponent
} from './components/summary/volume-summary/volume-summary.component';
import {
  DetectionSummaryComponent
} from './components/summary/detection-summary/detection-summary.component';
import { SplitViewSliderDirective } from './directives/split-view-slider';
import {
  ReportModeDialog
} from './dialogs/report-mode-dialog/report-mode.dialog';
import { MatStepperModule } from '@angular/material/stepper';
import {
  TrackerHistoryChannelComponents
} from './components/history-channel/tracker-history-channel.component';
import {
  FocusRemoverDirective
} from './directives/focus-remover';
import {
  ViewPointCloudSizeSliderComponent
} from './components/view-configuration/view-point-cloud-size-slider/view-point-cloud-size-slider.component';
import {
  CsvReaderDialog
} from './dialogs/csv-reader-dialog/csv-reader.dialog';
import { MatRadioModule } from '@angular/material/radio';
import {
  ReportFormatsDialog
} from './dialogs/report-formats-dialog/report-formats.dialog';
import {
  ItemListHeaderComponents
} from './components/item-list-header/item-list-header.component';
import {
  VolumeHeaderComponent
} from './components/volume-header/volume-header.component';
import {
  TheoricalLayerSelectorComponent
} from './components/theorical-layer-selector/theorical-layer-selector.component';
import {
  DetectionHistoryChannelComponents
} from './components/history-channel/detection-history-channel.components';
import { PanzoomDirective } from './directives/panzoom';
import {
  DetectionFiltersComponent
} from './components/item-filters/detection-filters/detection-filters.component';
import {
  ViewerDisplayPanelComponent
} from './components/viewer-display-panel/viewer-display-panel.component';
import {
  CommentSystemComponent
} from './components/comment-system/comment-system.component';
import {
  VolumeHistoryChannelComponents
} from './components/history-channel/volume-history-channel.components';
import {
  UploadButtonComponent
} from './components/upload-button/upload-button.component';
import {
  CommentUserComponent
} from './components/comment-user/comment-user.component';
import {
  CommentMentionComponent
} from './components/comment-mention/comment-mention.component';
import { AttachmentsCarouselComponent } from './components/attachments-carousel/attachments-carousel.component';
import { CutProfileDeviationSimilarityPipe } from './pipes/cut.pipe';
import {
  VolumeViewerComponent
} from './components/volume-viewer/volume-viewer.component';



@NgModule({
  declarations: [
    AttachmentsCarouselComponent,
    CommentMentionComponent,
    CommentUserComponent,
    UploadButtonComponent,
    CommentSystemComponent,
    DetectionFiltersComponent,
    ItemListHeaderComponents,
    VolumeHeaderComponent,
    ItemSearchComponent,
    SearchComponent,
    SiteSearchComponent,
    HistoryChannelComponents,
    VolumeHistoryChannelComponents,
    CutHistoryChannelComponents,
    DetectionHistoryChannelComponents,
    MeasureHistoryChannelComponents,
    TrackerHistoryChannelComponents,
    FilterSliderComponent,
    DensityChartComponent,
    MarkerCardComponent,
    CutCardComponent,
    MeasureCardComponent,
    VolumeDetailsComponent,
    MarkerDetailsComponent,
    DetectionDetailsComponent,
    MeasureDetailsComponent,
    ElevationDetailsComponent,
    ComputeLoaderComponent,
    VolumeThresholdFormComponent,
    VolumeDisplayModeSelectorComponent,
    Cesium2dvControlComponent,
    ViewModeSelectorComponent,
    VolumeColorRampComponent,
    CutDetailsComponent,
    CutListComponent,
    DetectionListComponent,
    MarkerListComponent,
    MeasureListComponent,
    VolumeListComponent,
    ElevationListComponent,
    CutChartComponent,
    ElevationChartComponent,
    DetectionCardComponent,
    VolumeCardComponent,
    ElevationCardComponent,
    TilesetDiffColorRampComponent,
    TilesetStyleFormComponent,
    ReferenceBatchSelectorComponent,
    ViewRepresentationSelectorComponent,
    ViewPointCloudSizeSliderComponent,
    WorldActionsBarComponent,
    VolumeSummaryComponent,
    DetectionSummaryComponent,
    TheoricalLayerSelectorComponent,
    ConfirmDialog,
    DeleteConfirmDialog,
    RenameDialog,
    UserConfigurationErrorDialog,
    DeleteDetectionDialog,
    CreateDetectionDialog,
    ReportDialog,
    LeaveSiteDialog,
    OrganizationDialog,
    AdvandcedActionStartDialog,
    AdvandcedActionStopDialog,
    BatchDiffRepresentationSelectorDialog,
    CreatePerreadPipelineDialog,
    ReportModeDialog,
    CsvReaderDialog,
    ReportFormatsDialog,
    DateSincePipe,
    DateFormatPipe,
    LinkifyPipe,
    MentionifyPipe,
    DurationPipe,
    ProductPermissionPipe,
    SearchPipe,
    FiltersPipe,
    SectorPipe,
    SentenceCasePipe,
    InspectionModulePipe,
    FormatDetectionModePipe,
    FormatDetectionStatusPipe,
    DistinctDetectionLabelsPipe,
    DetectionConfidencePipe,
    DetectionEvolutionPipe,
    DetectionStatusPipe,
    CesiumDirective,
    CesiumMiniMapDirective,
    SplitViewSliderDirective,
    FocusRemoverDirective,
    PanzoomDirective,
    MarkerSortDistancePipe,
    ItemNamePipe,
    ECEFToWGS84Pipe,
    DistinctDetectionCreateModesPipe,
    DetectionLabelPipe,
    CutProfileDeviationSimilarityPipe,
    ViewModeSelectorComponent,
    ViewerDisplayPanelComponent
  ],
  exports: [
    AttachmentsCarouselComponent,
    CommentMentionComponent,
    CommentUserComponent,
    UploadButtonComponent,
    CommentSystemComponent,
    DetectionFiltersComponent,
    ItemListHeaderComponents,
    VolumeHeaderComponent,
    ItemSearchComponent,
    SearchComponent,
    SiteSearchComponent,
    HistoryChannelComponents,
    VolumeHistoryChannelComponents,
    CutHistoryChannelComponents,
    DetectionHistoryChannelComponents,
    MeasureHistoryChannelComponents,
    TrackerHistoryChannelComponents,
    FilterSliderComponent,
    DensityChartComponent,
    VolumeDetailsComponent,
    MarkerDetailsComponent,
    ElevationDetailsComponent,
    DetectionDetailsComponent,
    MeasureDetailsComponent,
    ComputeLoaderComponent,
    VolumeThresholdFormComponent,
    VolumeDisplayModeSelectorComponent,
    Cesium2dvControlComponent,
    ViewModeSelectorComponent,
    ViewPointCloudSizeSliderComponent,
    VolumeColorRampComponent,
    CutDetailsComponent,
    CutListComponent,
    DetectionListComponent,
    MarkerListComponent,
    MeasureListComponent,
    VolumeListComponent,
    ElevationListComponent,
    CutChartComponent,
    ElevationChartComponent,
    DetectionCardComponent,
    VolumeCardComponent,
    ElevationCardComponent,
    TilesetDiffColorRampComponent,
    TilesetStyleFormComponent,
    ReferenceBatchSelectorComponent,
    WorldActionsBarComponent,
    VolumeSummaryComponent,
    DetectionSummaryComponent,
    TheoricalLayerSelectorComponent,
    ConfirmDialog,
    DeleteConfirmDialog,
    RenameDialog,
    LeaveSiteDialog,
    UserConfigurationErrorDialog,
    DeleteDetectionDialog,
    CreateDetectionDialog,
    ReportDialog,
    OrganizationDialog,
    AdvandcedActionStartDialog,
    AdvandcedActionStopDialog,
    BatchDiffRepresentationSelectorDialog,
    CreatePerreadPipelineDialog,
    ReportModeDialog,
    CsvReaderDialog,
    ReportFormatsDialog,
    DateSincePipe,
    DateFormatPipe,
    LinkifyPipe,
    MentionifyPipe,
    DurationPipe,
    SearchPipe,
    FiltersPipe,
    SectorPipe,
    SentenceCasePipe,
    InspectionModulePipe,
    ProductPermissionPipe,
    FormatDetectionModePipe,
    FormatDetectionStatusPipe,
    MarkerSortDistancePipe,
    ItemNamePipe,
    ECEFToWGS84Pipe,
    DistinctDetectionLabelsPipe,
    DistinctDetectionCreateModesPipe,
    DetectionConfidencePipe,
    DetectionEvolutionPipe,
    DetectionStatusPipe,
    DetectionLabelPipe,
    CutProfileDeviationSimilarityPipe,
    CesiumDirective,
    CesiumMiniMapDirective,
    SplitViewSliderDirective,
    FocusRemoverDirective,
    PanzoomDirective,
    ViewRepresentationSelectorComponent,
    MeasureCardComponent,
    CutCardComponent,
    MarkerCardComponent,
    ViewerDisplayPanelComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatDialogModule,
    MatExpansionModule,
    MatInputModule,
    MatButtonToggleModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCardModule,
    MatBadgeModule,
    MatSliderModule,
    MatMenuModule,
    MatTabsModule,
    MatStepperModule,
    ReactiveFormsModule,
    NgChartsModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatRadioModule,
    TranslocoModule,
    VolumeViewerComponent
  ],
  providers: [
    DateSincePipe,
    DateFormatPipe,
    LinkifyPipe,
    MentionifyPipe,
    DurationPipe,
    SearchPipe,
    FiltersPipe,
    SectorPipe,
    ProductPermissionPipe,
    InspectionModulePipe,
    FormatDetectionModePipe,
    FormatDetectionStatusPipe,
    MarkerSortDistancePipe,
    ItemNamePipe,
    ECEFToWGS84Pipe,
    SentenceCasePipe,
    DetectionLabelPipe,
  ]
})
export class SharedModule { }
